// src/components/Groups/EnhancedManageStudentsDialog.tsx

import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
} from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  GroupAdd as GroupAddIcon,
  School as SchoolIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import { Student } from "../../Types/Student";
import { Group } from "../../Types/Group";
import { useTeacher } from "../../context/TeacherContext";
import apiAxiosInstance, { aiAxiosInstance } from "../../utils/axiosInstance";
import GoogleClassroomImportForGroups from "./GoogleClassroomImportForGroups";
import { 
  ProcessedStudentWithDuplicates, 
  validateStudentFields 
} from "./utils/duplicateDetection";
import { simpleDuplicateValidator } from "./utils/simpleDuplicateValidation";
import SimpleEditableStudentGrid from "./SimpleEditableStudentGrid";

// Extend Window interface for TypeScript
declare global {
  interface Window {
    processTimeout?: NodeJS.Timeout;
  }
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`manage-students-tabpanel-${index}`}
    aria-labelledby={`manage-students-tab-${index}`}
  >
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

// Using ProcessedStudentWithDuplicates from duplicateDetection utils

interface ErrorResponse {
  error: string;
  details?: string;
  duplicates?: any[];
}

interface EnhancedManageStudentsDialogProps {
  open: boolean;
  onClose: () => void;
  selectedGroup: Group;
  allStudents: Student[];
  onStudentsAssigned: (studentIds: string[], groupId: string) => void;
  onRefreshStudents: () => void;
}

const EnhancedManageStudentsDialog: React.FC<EnhancedManageStudentsDialogProps> = ({
  open,
  onClose,
  selectedGroup,
  allStudents,
  onStudentsAssigned,
  onRefreshStudents,
}) => {
  const { teacher } = useTeacher();
  const [tabValue, setTabValue] = useState(0);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  
  // Single student form state
  const [singleStudent, setSingleStudent] = useState({
    firstName: '',
    lastName: '',
    middleName: '',
    gradeLevel: teacher?.gradeLevel || '',
    dateOfBirth: '',
    parentEmail: '',
    parentPhone: '',
  });
  
  // Form validation state
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  
  // Multiple students state
  const [textInput, setTextInput] = useState('');
  const [processedStudents, setProcessedStudents] = useState<ProcessedStudentWithDuplicates[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Initialize selected students when dialog opens
  useEffect(() => {
    if (open && selectedGroup) {
      // Handle both cases: students as objects or as IDs (strings)
      const groupStudentIds = selectedGroup.students?.map(s => 
        typeof s === 'string' ? s : s._id!
      ) || [];
      console.log('Selected Group Students:', selectedGroup.students);
      console.log('Extracted Student IDs:', groupStudentIds);
      setSelectedStudentIds(groupStudentIds);
    }
  }, [open, selectedGroup]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setTabValue(0);
      setSingleStudent({
        firstName: '',
        lastName: '',
        middleName: '',
        gradeLevel: teacher?.gradeLevel || '',
        dateOfBirth: '',
        parentEmail: '',
        parentPhone: '',
      });
      setValidationErrors({});
      setTextInput('');
      setProcessedStudents([]);
      setError(null);
      setSuccess(null);
      setValidationErrors({});
    }
  }, [open, teacher?.gradeLevel]);

  const handleToggleStudent = (studentId: string) => {
    setSelectedStudentIds(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSingleStudentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSingleStudent({
      ...singleStudent,
      [name]: value
    });
    
    // Real-time validation
    const errors = validateStudentFields(
      name === 'firstName' ? value : singleStudent.firstName,
      name === 'lastName' ? value : singleStudent.lastName,
      name === 'middleName' ? value : singleStudent.middleName,
      name === 'dateOfBirth' ? value : singleStudent.dateOfBirth,
      name === 'parentEmail' ? value : singleStudent.parentEmail,
      name === 'parentPhone' ? value : singleStudent.parentPhone
    );
    
    setValidationErrors(prev => ({
      ...prev,
      ...errors,
      // Clear error for the field being edited if it's now valid
      [name]: errors[name] || ''
    }));
  };

  const handleSingleStudentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teacher?._id) return;

    // Validate form
    const errors = validateStudentFields(
      singleStudent.firstName,
      singleStudent.lastName,
      singleStudent.middleName,
      singleStudent.dateOfBirth,
      singleStudent.parentEmail,
      singleStudent.parentPhone
    );
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Check for duplicates before saving
    const studentToValidate: ProcessedStudentWithDuplicates = {
      firstName: singleStudent.firstName,
      lastName: singleStudent.lastName,
      middleName: singleStudent.middleName,
      gradeLevel: singleStudent.gradeLevel || teacher.gradeLevel || '',
      dateOfBirth: singleStudent.dateOfBirth,
      parentEmail: singleStudent.parentEmail,
      parentPhone: singleStudent.parentPhone,
    };
    
    const validation = simpleDuplicateValidator.validateStudent(studentToValidate, allStudents);
    
    if (!validation.isValid) {
      setError({ 
        error: validation.message,
        details: validation.suggestions.join('\n')
      });
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const studentData = {
        ...singleStudent,
        middleName: singleStudent.middleName?.trim() || undefined,
        dateOfBirth: singleStudent.dateOfBirth?.trim() || undefined,
        parentEmail: singleStudent.parentEmail?.trim() || undefined,
        parentPhone: singleStudent.parentPhone?.trim() || undefined,
        teacherId: teacher._id,
        gradeLevel: singleStudent.gradeLevel || teacher.gradeLevel,
      };

      const response = await apiAxiosInstance.post('/api/students', studentData);
      const newStudent = response.data;
      
      // Automatically add to the selected group (without removing existing ones)
      if (selectedGroup?._id && newStudent._id) {
        await apiAxiosInstance.post(`/api/groups/${selectedGroup._id}/add-students`, {
          studentIds: [newStudent._id],
        });
      }

      setSuccess('Student added and assigned to group successfully!');
      setSingleStudent({
        firstName: '',
        lastName: '',
        middleName: '',
        gradeLevel: teacher.gradeLevel || '',
        dateOfBirth: '',
        parentEmail: '',
        parentPhone: '',
      });
      setValidationErrors({});
      
      // Refresh the students list
      onRefreshStudents();
      
    } catch (err: any) {
      console.error('Error:', err);
      setError({ error: 'Failed to add student. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = e.target.files?.[0];
    if (uploadedFile) {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const content = event.target?.result as string;
        setTextInput(content);
        
        // Auto-process the file content
        if (content.trim()) {
          await processStudentsFromText(content);
        }
      };
      reader.readAsText(uploadedFile);
    }
  };
  
  // Extract processing logic to reusable function
  const processStudentsFromText = async (text: string) => {
    if (!text.trim()) {
      setError({ error: 'Please enter student information' });
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const aiResponse = await aiAxiosInstance.post('/parse-students', {
        text: text,
        teacherGrade: teacher?.gradeLevel,
        teacherId: teacher?._id
      });

      const students = aiResponse.data.students || [];
      
      // Set processed students (no complex duplicate detection needed)
      setProcessedStudents(students);
      
      // Validate students using simple validation
      const { valid, invalid } = simpleDuplicateValidator.validateStudentBatch(students, allStudents);
      
      if (invalid.length > 0) {
        setSuccess(`Students processed! ${invalid.length} student${invalid.length > 1 ? 's' : ''} need additional information to avoid confusion with existing students. Edit them in the table below.`);
      } else {
        setSuccess(`Students processed successfully! All ${valid.length} students are ready to save.`);
      }
    } catch (err: any) {
      console.error('Error processing students:', err);
      setError({ error: 'Failed to process students. Please check the format and try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (window.processTimeout) {
        clearTimeout(window.processTimeout);
      }
    };
  }, []);
  
  // Remove processMultipleStudents function as it's no longer needed

  const handleSaveMultipleStudents = async () => {
    if (processedStudents.length === 0) {
      setError({ error: 'No students to save' });
      return;
    }

    if (!teacher?._id) {
      setError({ error: 'Teacher ID is required' });
      return;
    }

    // Validate all students - save only the valid ones
    const { valid, invalid } = simpleDuplicateValidator.validateStudentBatch(processedStudents, allStudents);
    
    if (valid.length === 0) {
      setError({ 
        error: `No students ready to save. All ${invalid.length} student${invalid.length > 1 ? 's' : ''} need additional information to distinguish them from existing students.` 
      });
      return;
    }

    setLoading(true);
    try {
      // Use valid students directly (they're already the correct subset)
      const studentsToSave = valid.map(student => ({
        ...student,
        teacherId: teacher._id,
        gradeLevel: student.gradeLevel || teacher.gradeLevel,
        // Ensure all fields are properly included
        middleName: student.middleName?.trim() || undefined,
        dateOfBirth: student.dateOfBirth?.trim() || undefined,
        parentEmail: student.parentEmail?.trim() || undefined,
        parentPhone: student.parentPhone?.trim() || undefined,
      }));
      
      console.log('Saving students with data:', studentsToSave); // Debug log

      const response = await apiAxiosInstance.post('/api/students/bulk-create', {
        students: studentsToSave
      });

      const createdStudents = response.data.students || [];
      const actualSavedCount = createdStudents.length;
      
      // Automatically add all created students to the selected group (without removing existing ones)
      if (selectedGroup?._id && createdStudents.length > 0) {
        const studentIds = createdStudents.map((s: Student) => s._id).filter(Boolean);
        if (studentIds.length > 0) {
          await apiAxiosInstance.post(`/api/groups/${selectedGroup._id}/add-students`, {
            studentIds,
          });
        }
      }

      // Refresh the students list FIRST before filtering
      onRefreshStudents();
      
      // Get updated student list to check which ones were saved
      const studentsResponse = await apiAxiosInstance.get('/api/students');
      const updatedAllStudents = studentsResponse.data || [];
      
      // Now filter out the saved students by checking if they exist in the updated student list
      const remainingStudents = processedStudents.filter(processedStudent => {
        // Check if this processed student now exists in the database
        const existsInDb = updatedAllStudents.some((dbStudent: Student) => 
          dbStudent.firstName.toLowerCase() === processedStudent.firstName.toLowerCase() &&
          dbStudent.lastName.toLowerCase() === processedStudent.lastName.toLowerCase() &&
          ((!dbStudent.middleName && !processedStudent.middleName) || 
           (dbStudent.middleName?.toLowerCase() === processedStudent.middleName?.toLowerCase())) &&
          dbStudent.gradeLevel === (processedStudent.gradeLevel || teacher.gradeLevel)
        );
        // Keep students that were NOT saved (don't exist in DB or were invalid)
        return !existsInDb;
      });
      
      setProcessedStudents(remainingStudents);
      
      // Provide detailed feedback about what was saved vs ignored
      let successMessage = `Successfully added ${actualSavedCount} student${actualSavedCount > 1 ? 's' : ''} to the group!`;
      
      if (remainingStudents.length > 0) {
        successMessage += ` ${remainingStudents.length} student${remainingStudents.length > 1 ? 's' : ''} remaining - needs additional information.`;
        // Keep text input if there are remaining students to edit
      } else {
        // All students were saved, clear text input too
        setTextInput('');
      }
      
      setSuccess(successMessage);
      
    } catch (err: any) {
      console.error('Error:', err);
      setError({ error: 'Failed to save students. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveProcessedStudent = (index: number) => {
    setProcessedStudents(prev => prev.filter((_, i) => i !== index));
  };

  // Removed auto-resolve - teachers have full control through editing

  const handleAssignExistingStudents = () => {
    if (selectedGroup?._id) {
      onStudentsAssigned(selectedStudentIds, selectedGroup._id);
      onClose();
    }
  };

  const handleGoogleClassroomSuccess = () => {
    setSuccess('Students imported from Google Classroom successfully!');
    onRefreshStudents();
  };

  // Check if there are no students at all
  const hasNoStudents = allStudents.length === 0;
  
  // State to force showing tabs even when no students exist
  const [forceShowTabs, setForceShowTabs] = useState(false);
  
  // Handlers for empty state buttons
  const handleAddSingleStudent = () => {
    setForceShowTabs(true);
    setTabValue(1);
  };
  
  const handleAddMultipleStudents = () => {
    setForceShowTabs(true);
    setTabValue(2);
  };
  
  const handleImportFromGoogle = () => {
    setForceShowTabs(true);
    setTabValue(3);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      aria-labelledby="enhanced-manage-students-dialog-title"
    >
      <DialogTitle id="enhanced-manage-students-dialog-title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Manage Students for "{selectedGroup?.name || 'Selected Group'}"
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.error}
            {error.details && (
              <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-line' }}>
                {error.details}
              </Typography>
            )}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {hasNoStudents && !forceShowTabs ? (
          // Empty state when no students exist
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <SchoolIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No students found for your account
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add students to get started with group management. You can add them individually, 
              in bulk, or import from Google Classroom.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={handleAddSingleStudent}
              >
                Add Single Student
              </Button>
              <Button
                variant="outlined"
                startIcon={<GroupAddIcon />}
                onClick={handleAddMultipleStudents}
              >
                Add Multiple Students
              </Button>
              <Button
                variant="outlined"
                startIcon={<SchoolIcon />}
                onClick={handleImportFromGoogle}
              >
                Import from Google Classroom
              </Button>
            </Box>
          </Box>
        ) : (
          // Normal tabbed interface when students exist
          <Box>
            <Tabs 
              value={tabValue} 
              onChange={(e, newValue) => setTabValue(newValue)} 
              sx={{ mb: 2 }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab 
                label="Assign Existing" 
                icon={<GroupAddIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Add Single Student" 
                icon={<PersonAddIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Add Multiple Students" 
                icon={<AddIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Google Classroom" 
                icon={<SchoolIcon />} 
                iconPosition="start"
              />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              {hasNoStudents ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    No existing students to assign.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add students using the other tabs, then return here to assign them to groups.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select students to assign to this group. Currently assigned students are pre-selected.
                  </Typography>
                  
                  <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {allStudents.map((student) => (
                  <ListItem key={student._id} dense>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedStudentIds.includes(student._id!)}
                          onChange={() => handleToggleStudent(student._id!)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" component="div">
                            {student.firstName}
                            {student.middleName && ` ${student.middleName}`}
                            {` ${student.lastName}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" component="div">
                            {student.parentEmail && `Email: ${student.parentEmail}`}
                            {student.gradeLevel && ` • Grade: ${student.gradeLevel}`}
                            {student.dateOfBirth && ` • DOB: ${student.dateOfBirth}`}
                          </Typography>
                        </Box>
                      }
                      sx={{ width: '100%', alignItems: 'flex-start' }}
                    />
                  </ListItem>
                ))}
                  </List>
                </>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add a single student and automatically assign them to this group. 
                Student ID will be automatically generated. Additional information helps prevent duplicate entries.
              </Typography>
              
              <form onSubmit={handleSingleStudentSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="firstName"
                      value={singleStudent.firstName}
                      onChange={handleSingleStudentChange}
                      required
                      error={!!validationErrors.firstName}
                      helperText={validationErrors.firstName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="lastName"
                      value={singleStudent.lastName}
                      onChange={handleSingleStudentChange}
                      required
                      error={!!validationErrors.lastName}
                      helperText={validationErrors.lastName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Middle Name (Optional)"
                      name="middleName"
                      value={singleStudent.middleName}
                      onChange={handleSingleStudentChange}
                      error={!!validationErrors.middleName}
                      helperText={validationErrors.middleName || "Helps distinguish students with similar names"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Grade Level"
                      name="gradeLevel"
                      value={singleStudent.gradeLevel}
                      onChange={handleSingleStudentChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Date of Birth (Optional)"
                      name="dateOfBirth"
                      type="date"
                      value={singleStudent.dateOfBirth}
                      onChange={handleSingleStudentChange}
                      InputLabelProps={{ shrink: true }}
                      error={!!validationErrors.dateOfBirth}
                      helperText={validationErrors.dateOfBirth || "Helps prevent duplicate students"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Parent Email (Optional)"
                      name="parentEmail"
                      type="email"
                      value={singleStudent.parentEmail}
                      onChange={handleSingleStudentChange}
                      error={!!validationErrors.parentEmail}
                      helperText={validationErrors.parentEmail || "For communication and duplicate prevention"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Parent Phone (Optional)"
                      name="parentPhone"
                      type="tel"
                      value={singleStudent.parentPhone}
                      onChange={handleSingleStudentChange}
                      error={!!validationErrors.parentPhone}
                      helperText={validationErrors.parentPhone || "For communication and duplicate prevention"}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
                    >
                      Add Student to Group
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add multiple students at once and automatically assign them to this group.
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={6}
                value={textInput}
                onChange={(e) => {
                  setTextInput(e.target.value);
                  // Auto-process when user stops typing (debounced)
                  if (e.target.value.trim()) {
                    clearTimeout(window.processTimeout);
                    window.processTimeout = setTimeout(() => {
                      processStudentsFromText(e.target.value);
                    }, 2000); // 2 second delay after user stops typing
                  } else {
                    // Clear processed students if text is empty
                    setProcessedStudents([]);
                  }
                }}
                placeholder={`Paste or type student information here...

Supported formats:
- Free text describing students
- CSV format: FirstName,LastName,GradeLevel
- One student per line

Students will be processed automatically as you type.`}
                disabled={loading}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="text/plain,text/csv,.csv"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                
                <Button
                  variant="outlined"
                  onClick={() => fileInputRef.current?.click()}
                  startIcon={<DescriptionIcon />}
                  disabled={loading}
                >
                  Upload File
                </Button>
                
                {loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      Processing student information...
                    </Typography>
                  </Box>
                )}
              </Box>

              {processedStudents.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <SimpleEditableStudentGrid
                    students={processedStudents}
                    existingStudents={allStudents}
                    onStudentsChange={setProcessedStudents}
                    onRemoveStudent={handleRemoveProcessedStudent}
                  />
                  {/* Save button moved to DialogActions */}
                </Box>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              {selectedGroup?._id ? (
                <GoogleClassroomImportForGroups
                  selectedGroupId={selectedGroup._id}
                  onImportSuccess={handleGoogleClassroomSuccess}
                />
              ) : (
                <Typography color="error">
                  Group information not available. Please close and try again.
                </Typography>
              )}
            </TabPanel>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Show save message and button for multiple students tab */}
          {tabValue === 2 && processedStudents.length > 0 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Students with duplicate information will be ignored
              </Typography>
              <Button
                variant="contained"
                onClick={handleSaveMultipleStudents}
                disabled={loading || simpleDuplicateValidator.validateStudentBatch(processedStudents, allStudents).valid.length === 0}
                size="large"
              >
                {(() => {
                  const { valid } = simpleDuplicateValidator.validateStudentBatch(processedStudents, allStudents);
                  return valid.length > 0 
                    ? `Save Ready Students (${valid.length})`
                    : 'No Students Ready';
                })()}
              </Button>
            </>
          )}
          
          {/* Show assign button for existing students tab */}
          {(!hasNoStudents || forceShowTabs) && tabValue === 0 && (
            <Button
              onClick={handleAssignExistingStudents}
              variant="contained"
              color="primary"
            >
              Assign Selected Students
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EnhancedManageStudentsDialog;
